import { type ReactNode } from 'react';
import { matchPath } from 'react-router-dom';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  type ResourceDisplayNameProps,
  createResourceLinkExtension,
} from '@spotify/confidence-core-react';

import { useRecording } from '../hooks';
import { recordingsPlugin } from '../plugin';
import { recordingFindingRouteRef, recordingRouteRef } from '../routes';

type RecordingResourceLinkProps = {
  name: string;
  text: ReactNode;
  className?: string;
};

const matchResourceRoute = (name: string, routePath: string) =>
  matchPath({ path: `/recordings${routePath}`, end: true }, `/${name}`);

const isRecordingResourceName = (name: string): boolean =>
  [recordingRouteRef.path, recordingFindingRouteRef.path].some(routePath =>
    Boolean(matchResourceRoute(name, routePath)),
  );

const RecordingResourceDisplayName = ({
  name,
  children,
}: ResourceDisplayNameProps) => {
  const findingMatch = matchResourceRoute(name, recordingFindingRouteRef.path);
  const recordingMatch = matchResourceRoute(name, recordingRouteRef.path);
  const recordingId = findingMatch?.params.id ?? recordingMatch?.params.id;
  const findingId = findingMatch?.params.findingId;
  const recordingName = recordingId ? `recordings/${recordingId}` : '';
  const { recording } = useRecording({
    name: recordingName,
    skip: !recordingId,
  });
  const findingSummary = recording?.analysis?.findings.find(
    finding => finding.id === findingId,
  )?.summary;

  return <>{findingSummary || recording?.displayName || children}</>;
};

const RecordingResourceLink = ({
  name,
  text,
  className,
}: RecordingResourceLinkProps) => {
  const recordingRoute = useRouteRef(recordingRouteRef);
  const recordingFindingRoute = useRouteRef(recordingFindingRouteRef);
  const findingMatch = matchResourceRoute(name, recordingFindingRouteRef.path);
  const recordingMatch = matchResourceRoute(name, recordingRouteRef.path);

  const recordingId = findingMatch?.params.id ?? recordingMatch?.params.id;
  const findingId = findingMatch?.params.findingId;
  let path: string | undefined;
  if (recordingId && findingId) {
    path = recordingFindingRoute({ id: recordingId, findingId });
  } else if (recordingId) {
    path = recordingRoute({ id: recordingId });
  }

  if (!path) {
    return <>{text}</>;
  }

  return (
    <Link to={path} className={className}>
      {text}
    </Link>
  );
};

export const RecordingResourceLinkExtension = recordingsPlugin.provide(
  createResourceLinkExtension({
    id: 'recording-resource-link',
    predicate: ({ name }) => isRecordingResourceName(name),
    DisplayName: RecordingResourceDisplayName,
    icon: 'recording',
    render: ({ name, text, className }) => (
      <RecordingResourceLink name={name} text={text} className={className} />
    ),
  }),
);
